// Pure helpers for the job page's auto-rollback watch and breach tabs
// (getRollbackWatches, getRollbackBreaches, getRollbackDoc).

import { RollbackDoc, WatchSignalSummary, WatchSummary } from '../../types/DeploymentRecord';
import { sortByEnvRegion } from './rollbackProfiles';

/** Watches asked for per page; the backend allows up to 25. */
export const WATCHES_PAGE_SIZE = 10;

/** A list slice is per job and env filter, so a filter never shows another's rows. */
export const rollbackListKey = (app: string, jobId: string, envRegion?: string): string =>
  `${app}/${jobId}|${envRegion || ''}`;

export type TierColor = 'error' | 'warning' | 'info' | 'default';

export const tierColor = (tier?: string): TierColor => {
  switch (tier) {
    case 'rollback':
      return 'error';
    case 'rca':
      return 'warning';
    case 'detect':
      return 'info';
    default:
      return 'default';
  }
};

/** A reply to a `before` request is an older page; anything else replaces. */
export const mergePage = <T>(existing: T[], incoming: T[], older: boolean): T[] =>
  older ? [...existing, ...incoming] : incoming;

/** Env/region filter options: those seen so far plus the selected one, in ladder order. */
export const envRegionChoices = (seen: string[], selected?: string): string[] => {
  const set = new Set(seen.filter(Boolean));
  if (selected) set.add(selected);
  return sortByEnvRegion(Array.from(set).map(env_region => ({ env_region }))).map(e => e.env_region);
};

/** getRollbackDoc's `data` pieces of one reply, joined in chunk order. */
export const assembleDocPieces = (pieces: Array<{ chunk?: number; data: string }>): string =>
  [...pieces]
    .sort((a, b) => (a.chunk ?? 0) - (b.chunk ?? 0))
    .map(p => p.data)
    .join('');

export const parseRollbackDoc = (text: string): RollbackDoc => {
  const doc = JSON.parse(text);
  if (!doc || typeof doc !== 'object' || Array.isArray(doc)) throw new Error('Malformed document');
  return doc as RollbackDoc;
};

/** A front-matter value as text: null as "—", a map as "k: v" lines. */
export const formatMetaValue = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.map(formatMetaValue).join(', ');
  if (typeof v === 'object') {
    const entries = Object.entries(v as Record<string, unknown>);
    return entries.length ? entries.map(([k, x]) => `${k}: ${formatMetaValue(x)}`).join('\n') : '—';
  }
  return String(v);
};

export const metaString = (meta: Record<string, unknown> | null | undefined, key: string): string | undefined => {
  const v = meta?.[key];
  return v === null || v === undefined || v === '' ? undefined : String(v);
};

/** "prev → new", or whichever side is known. */
export const versionChange = (previous?: string, version?: string): string => {
  if (previous && version) return `${previous} → ${version}`;
  return version || previous || '—';
};

/** A case with no human or agent verdict yet is a draft. */
export const verdictLabel = (meta: Record<string, unknown> | null | undefined): string =>
  metaString(meta, 'verdict') || 'draft';

export interface SignalRow extends WatchSignalSummary {
  signal: string;
}

export const summarySignalRows = (summary: WatchSummary | null | undefined): SignalRow[] =>
  Object.entries(summary?.signals || {})
    .map(([signal, s]) => ({ signal, ...s }))
    .sort((a, b) => a.signal.localeCompare(b.signal));

export const thresholdRows = (summary: WatchSummary | null | undefined): Array<{ name: string; value: number }> =>
  Object.entries(summary?.thresholds || {})
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => a.name.localeCompare(b.name));
